import { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Checkbox } from 'components/ui/checkbox';
import type { Expertise, Specialty } from 'api/referentialsApi';
import {
  useCreateSpecialty,
  useUpdateSpecialty,
  useCreateExpertise,
  useAttachExpertise,
  useUpdateExpertise,
  useDetachExpertise,
} from 'hooks/useReferentials';
import { slugify } from 'utils/slug';
import { apiErrorMessage } from 'utils/errorMessage';
import { useToast } from 'components/ui/toast';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import { detachCopy } from '../confirmCopy';

export interface SpecialtyFormDialogProps {
  open: boolean;
  /** null = creation. */
  specialty: Specialty | null;
  /** Every expertise in the referential, so an existing one can be attached rather than rejected. */
  allExpertises: Expertise[];
  onClose: () => void;
}

interface State {
  name: string;
  isActive: boolean;
  /** Name typed in the "add an expertise" row, not yet submitted. */
  draftExpertise: string;
  error: string | null;
  /** Expertise the ops asked to detach, held until the warning is confirmed. */
  detaching: Expertise | null;
}

type Action =
  | { type: 'reset'; specialty: Specialty | null }
  | { type: 'setName'; name: string }
  | { type: 'setActive'; isActive: boolean }
  | { type: 'setDraft'; draftExpertise: string }
  | { type: 'setError'; error: string | null }
  | { type: 'askDetach'; expertise: Expertise | null };

const initialState: State = { name: '', isActive: true, draftExpertise: '', error: null, detaching: null };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'reset':
      return {
        ...initialState,
        name: action.specialty?.name ?? '',
        isActive: action.specialty?.is_active ?? true,
      };
    case 'setName':
      return { ...state, name: action.name, error: null };
    case 'setActive':
      return { ...state, isActive: action.isActive };
    case 'setDraft':
      return { ...state, draftExpertise: action.draftExpertise, error: null };
    case 'setError':
      return { ...state, error: action.error };
    case 'askDetach':
      return { ...state, detaching: action.expertise, error: null };
    default:
      return state;
  }
};

/**
 * Create or edit a specialty, and manage the expertises hanging under it — they are created from
 * here and nowhere else. Expertise writes apply immediately (the specialty must exist to attach
 * to), while the specialty's own fields are saved on submit.
 */
const SpecialtyFormDialog = ({ open, specialty, allExpertises, onClose }: SpecialtyFormDialogProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, dispatch] = useReducer(reducer, initialState);
  const createSpecialty = useCreateSpecialty();
  const updateSpecialty = useUpdateSpecialty();
  const createExpertise = useCreateExpertise();
  const attachExpertise = useAttachExpertise();
  const updateExpertise = useUpdateExpertise();
  const detachExpertise = useDetachExpertise();

  // Reset when the dialog opens or targets another row — never on a refetch. The prop is a live
  // object that changes identity on every invalidation, and resetting then would wipe the name
  // being typed the moment an expertise is added.
  const specialtyId = specialty?.id ?? null;
  useEffect(() => {
    if (open) dispatch({ type: 'reset', specialty });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, specialtyId]);

  const saving = createSpecialty.isPending || updateSpecialty.isPending;
  const expertises = specialty?.expertises ?? [];

  const submit = async () => {
    const name = state.name.trim();
    if (!name) {
      dispatch({ type: 'setError', error: t('A name is required') });
      return;
    }
    try {
      if (specialty) {
        await updateSpecialty.mutateAsync({ id: specialty.id, data: { name, isActive: state.isActive } });
      } else {
        await createSpecialty.mutateAsync({ code: slugify(name), name });
      }
      toast.success(specialty ? t('{{name}} saved', { name }) : t('{{name}} created', { name }));
      onClose();
    } catch (error) {
      // The dialog stays open on failure: closing it would lose what was typed and hide the reason.
      dispatch({ type: 'setError', error: apiErrorMessage(t, error) });
    }
  };

  /**
   * Typing the name of an expertise that already exists attaches it here instead of failing on
   * the duplicate code. That is the only way an ops can reproduce the shared case the referential
   * is built around — Pose PLV under both Fabrication PLV and Installation.
   */
  const addExpertise = async () => {
    const name = state.draftExpertise.trim();
    if (!name || !specialty) return;
    const code = slugify(name);
    const existing = allExpertises.find((expertise) => expertise.code === code);
    try {
      if (existing) {
        await attachExpertise.mutateAsync({ expertiseId: existing.id, specialtyId: specialty.id });
      } else {
        await createExpertise.mutateAsync({ code, name, specialtyIds: [specialty.id] });
      }
      toast.success(existing ? t('{{name}} attached', { name: existing.name }) : t('{{name}} created', { name }));
      dispatch({ type: 'setDraft', draftExpertise: '' });
    } catch (error) {
      dispatch({ type: 'setError', error: apiErrorMessage(t, error) });
    }
  };

  // The mock-up detaches without a word. A shared expertise also hangs under other specialties,
  // so an ops tidying one of them up would break the others without seeing it — hence the warning.
  /**
   * Renaming an expertise from its specialty's dialog — the mock-up edits the line in place, and
   * without it a typo in an ops-entered label is unfixable from the interface. Committed on blur
   * rather than on every keystroke: one write per correction, not one per character.
   */
  const renameExpertise = async (expertise: Expertise, nextName: string) => {
    const name = nextName.trim();
    if (!name || name === expertise.name) return;
    try {
      await updateExpertise.mutateAsync({ id: expertise.id, data: { name } });
      toast.success(t('{{name}} saved', { name }));
    } catch (error) {
      dispatch({ type: 'setError', error: apiErrorMessage(t, error) });
    }
  };

  const confirmDetach = async () => {
    if (!specialty || !state.detaching) return;
    try {
      const detached = state.detaching.name;
      await detachExpertise.mutateAsync({ expertiseId: state.detaching.id, specialtyId: specialty.id });
      toast.success(t('{{name}} detached', { name: detached }));
      dispatch({ type: 'askDetach', expertise: null });
    } catch (error) {
      dispatch({ type: 'setError', error: apiErrorMessage(t, error) });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{specialty ? t('Edit specialty') : t('New specialty')}</DialogTitle>
          <DialogDescription>
            {t('Expertises are created from their specialty, never on their own screen.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="specialty-name">{t('Specialty name')}</Label>
            <Input
              id="specialty-name"
              value={state.name}
              onChange={(event) => dispatch({ type: 'setName', name: event.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('Expertises')}</Label>
            {!specialty && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('Save the specialty first, then add its expertises.')}
              </p>
            )}
            {specialty && expertises.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('No expertise yet.')}</p>
            )}
            {specialty &&
              expertises.map((expertise) => (
                <div key={expertise.id} className="flex items-center gap-2">
                  <Input
                    defaultValue={expertise.name}
                    aria-label={t('Expertise name')}
                    disabled={updateExpertise.isPending}
                    onBlur={(event) => renameExpertise(expertise, event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
                    className="flex-1"
                  />
                  {expertise.specialty_ids.length > 1 && (
                    <AlertTriangle
                      className="h-4 w-4 shrink-0 text-amber-500"
                      aria-label={t('Shared with another specialty')}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => dispatch({ type: 'askDetach', expertise })}
                    disabled={detachExpertise.isPending}
                    className="rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-950/40"
                    title={t('Detach')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            {specialty && (
              <div className="flex items-center gap-2">
                <Input
                  value={state.draftExpertise}
                  // A placeholder is not an accessible name: it disappears on the first keystroke.
                  aria-label={t('Add an expertise')}
                  placeholder={t('Add an expertise')}
                  onChange={(event) => dispatch({ type: 'setDraft', draftExpertise: event.target.value })}
                  onKeyDown={(event) => event.key === 'Enter' && addExpertise()}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addExpertise}
                  disabled={createExpertise.isPending || attachExpertise.isPending || !state.draftExpertise.trim()}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {t('Add')}
                </Button>
              </div>
            )}
          </div>

          {specialty && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={state.isActive}
                onCheckedChange={(checked: boolean) => dispatch({ type: 'setActive', isActive: checked })}
              />
              {t('Specialty active')}
            </label>
          )}

          {state.error && <p className="text-sm text-red-600">{state.error}</p>}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('Cancel')}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? t('Saving…') : t('Save')}
          </Button>
        </div>

        {state.detaching && (
          <ConfirmDeleteDialog
            open
            tone="warning"
            {...detachCopy(t, state.detaching)}
            loading={detachExpertise.isPending}
            error={state.error}
            onClose={() => dispatch({ type: 'askDetach', expertise: null })}
            onConfirm={confirmDetach}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SpecialtyFormDialog;
